import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { EXAMPLE_JSON } from '../utils/exampleTripData';
import { TripCardSkeleton } from '../components/Skeleton';

// ─── Drawer ───────────────────────────────────────────────────────────────────

function JsonDrawer({ open, onClose, onLoad }) {
    const { t } = useTranslation();
    const [activeTab, setActiveTab]     = useState('upload');
    const [jsonText, setJsonText]       = useState('');
    const [errorMsg, setErrorMsg]       = useState('');
    const fileInputRef = useRef(null);

    const GUIDE_FIELDS = [
        { field: 'trips[].name *',                 key: 'tripName' },
        { field: 'trips[].destination *',          key: 'destination' },
        { field: 'trips[].startDate / endDate *',  key: 'dates' },
        { field: 'trips[].image',                  key: 'image' },
        { field: 'trips[].currency',               key: 'currency' },
        { field: 'trips[].budget',                 key: 'budget' },
        { field: 'cities[].hotel',                 key: 'hotel' },
        { field: 'days[].date *',                  key: 'dayDate' },
        { field: 'activities[].type *',            key: 'activityType' },
        { field: 'activities[].cost',              key: 'cost' },
        { field: 'activities[].from / to / distance', key: 'transport' },
    ];

    const reset = () => { setActiveTab('upload'); setJsonText(''); setErrorMsg(''); };

    const handleClose = () => { reset(); onClose(); };

    const handleFile = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => {
            try   { onLoad(JSON.parse(ev.target?.result)); reset(); }
            catch { setErrorMsg(t('jsonDrawer.upload.errorInvalid')); }
        };
        reader.readAsText(file);
    };

    const handleTextSubmit = () => {
        try   { onLoad(JSON.parse(jsonText)); reset(); }
        catch { setErrorMsg(t('jsonDrawer.text.errorInvalid')); }
    };

    const TABS = [
        { id: 'upload', label: t('jsonDrawer.tabs.upload') },
        { id: 'text',   label: t('jsonDrawer.tabs.text') },
        { id: 'guide',  label: t('jsonDrawer.tabs.guide') },
    ];

    return (
        <>
            {/* Backdrop */}
            {open && <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40" onClick={handleClose} />}

            {/* Panel */}
            <div className={`fixed top-0 right-0 h-full z-50 w-full max-w-lg transform transition-transform duration-300 ease-in-out ${open ? 'translate-x-0' : 'translate-x-full'}`}>
                <div className="h-full bg-white dark:bg-gray-800 shadow-2xl flex flex-col">

                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                        <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">{t('jsonDrawer.title')}</h2>
                        <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors">
                            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* Tabs */}
                    <div className="border-b border-gray-200 dark:border-gray-700 flex-shrink-0 px-4 flex">
                        {TABS.map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`py-3 px-3 text-sm font-semibold border-b-2 transition-all duration-200 ${
                                    activeTab === tab.id
                                        ? 'border-primary-600 text-primary-700 dark:text-primary-400 dark:border-primary-400'
                                        : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                                }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {/* Body */}
                    <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-4">
                        {errorMsg && (
                            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm">
                                {errorMsg}
                            </div>
                        )}

                        {/* Upload file */}
                        {activeTab === 'upload' && (
                            <>
                                <p className="text-gray-600 dark:text-gray-400 text-sm">{t('jsonDrawer.upload.description')}</p>
                                <input ref={fileInputRef} type="file" accept=".json" onChange={handleFile} className="hidden" />
                                <button onClick={() => fileInputRef.current?.click()} className="btn-primary w-full py-3">
                                    {t('jsonDrawer.upload.button')}
                                </button>
                            </>
                        )}

                        {/* Text input */}
                        {activeTab === 'text' && (
                            <>
                                <p className="text-gray-600 dark:text-gray-400 text-sm">{t('jsonDrawer.text.description')}</p>
                                <textarea
                                    value={jsonText}
                                    onChange={e => setJsonText(e.target.value)}
                                    placeholder='{"trips": [...]}'
                                    className="input min-h-[400px] font-mono text-sm resize-y"
                                />
                            </>
                        )}

                        {/* Guide */}
                        {activeTab === 'guide' && (
                            <>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    {t('jsonDrawer.guide.description')}{' '}
                                    <span className="inline-block mx-0.5 px-1.5 py-0.5 rounded text-xs font-bold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">*</span>{' '}
                                    {t('jsonDrawer.guide.required')}
                                </p>

                                <div className="flex flex-col gap-2">
                                    {GUIDE_FIELDS.map(({ field, key }) => (
                                        <div key={field} className="flex items-start gap-2">
                                            <code className="flex-shrink-0 px-1.5 py-0.5 rounded text-xs font-mono bg-gray-100 dark:bg-gray-900 text-primary-700 dark:text-primary-400 whitespace-nowrap">
                                                {field}
                                            </code>
                                            <span className="text-xs text-gray-600 dark:text-gray-400">{t(`jsonDrawer.guide.fields.${key}`)}</span>
                                        </div>
                                    ))}
                                </div>

                                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-2">
                                    {t('jsonDrawer.guide.exampleTitle')}
                                </p>
                                <pre className="text-xs font-mono bg-gray-900 dark:bg-gray-950 text-emerald-300 rounded-xl p-4 overflow-auto max-h-72 border border-gray-700">
                                    {EXAMPLE_JSON}
                                </pre>

                                <button
                                    onClick={() => { setJsonText(EXAMPLE_JSON); setActiveTab('text'); }}
                                    className="btn-outline text-sm"
                                >
                                    {t('jsonDrawer.guide.useExample')}
                                </button>
                            </>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
                        <button onClick={handleClose} className="btn-ghost">{t('jsonDrawer.cancel')}</button>
                        <button onClick={handleTextSubmit} className="btn-primary">{t('jsonDrawer.load')}</button>
                    </div>
                </div>
            </div>
        </>
    );
}

// ─── Trip card ────────────────────────────────────────────────────────────────

function TripCard({ trip, onClick }) {
    const { t, i18n } = useTranslation();
    const locale = i18n.language === 'en' ? 'en-US' : 'it-IT';
    return (
        <div
            onClick={onClick}
            className="card p-6 flex flex-col gap-3 cursor-pointer hover:shadow-xl hover:scale-[1.02] transition-all duration-300"
        >
            <div className="flex items-start justify-between">
                <span className="text-4xl">{trip.image}</span>
            </div>

            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">{trip.name}</h3>
            <p className="text-gray-600 dark:text-gray-400">{trip.destination}</p>

            <div className="flex items-center gap-2 flex-wrap">
                {[trip.startDate, trip.endDate].map((d, i) => (
                    <React.Fragment key={i}>
                        {i === 1 && <span className="text-gray-400">→</span>}
                        <span className="badge bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300">
                            {new Date(d).toLocaleDateString(locale)}
                        </span>
                    </React.Fragment>
                ))}
            </div>

            {trip.budget != null && trip.budget !== 0 && (
                <p className="font-bold text-primary-700 dark:text-primary-400">
                    {t('homepage.budget')} {trip.currency || '€'}{trip.budget}
                </p>
            )}
        </div>
    );
}

// ─── Homepage ─────────────────────────────────────────────────────────────────

export default function Homepage({ onSelectTrip, tripsData, setSessionToken, sessionToken, loading }) {
    const { t } = useTranslation();
    const [data, setData]           = useState(tripsData || { trips: [] });
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [copied, setCopied]       = useState(false);

    useEffect(() => { if (tripsData) setData(tripsData); }, [tripsData]);

    const handleShare = () => {
        const url = `${window.location.origin}/?token=${sessionToken}`;
        navigator.clipboard.writeText(url).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <div className="max-w-7xl mx-auto px-4 py-8 flex flex-col gap-8">
            {/* Hero */}
            <div className="text-center flex flex-col gap-3">
                <h1 className="text-4xl md:text-5xl font-extrabold bg-gradient-to-r from-primary-600 to-primary-400 bg-clip-text text-transparent">
                    {t('homepage.title')}
                </h1>
                <p className="text-lg text-gray-600 dark:text-gray-400">
                    {t('homepage.subtitle')}
                </p>
            </div>

            {/* Actions */}
            <div className="flex justify-center gap-3 flex-wrap">
                {sessionToken && (
                    <button onClick={() => setSessionToken('')} className="btn-outline text-sm">
                        {t('auth.changePassword')}
                    </button>
                )}
                {sessionToken && (
                    <button onClick={handleShare} className="btn-outline text-sm">
                        {copied ? t('auth.linkCopied') : t('auth.shareLink')}
                    </button>
                )}
                <button onClick={() => setDrawerOpen(true)} className="btn-outline text-sm">
                    {t('homepage.loadJson')}
                </button>
            </div>

            {/* Drawer */}
            <JsonDrawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                onLoad={(json) => {
                    // Accetta sia {"trips":[...]} che un singolo oggetto trip
                    const trips = json.trips ?? [json];
                    // Normalizza nomi campo per compatibilità (text→title, label→title)
                    const normalized = trips.map(t => ({
                        ...t,
                        todos: (t.todos ?? []).map(td => ({ ...td, title: td.title ?? td.text ?? '' })),
                        utilsLinks: (t.utilsLinks ?? []).map(l => ({ ...l, title: l.title ?? l.label ?? '' })),
                    }));
                    setData({ trips: normalized });
                    setDrawerOpen(false);
                }}
            />

            {/* Trip grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {loading && data.trips.length === 0
                    ? [1, 2, 3].map(i => <TripCardSkeleton key={i} />)
                    : data.trips.map(trip => (
                        <TripCard key={trip.id} trip={trip} onClick={() => onSelectTrip(trip)} />
                    ))
                }
            </div>
        </div>
    );
}
